import React, { useState, useEffect, useRef, useContext } from 'react';
import { Color, Palette, ColorLibraryData } from '../../types/ColorLibraryModel';
import { UserContext } from '../ContextProviders';
import { saveToScrapbook } from '../../api/scrapbook-data';
import FolderSelectionDialog from '../scrapbook/dialogs/FolderSelectionDialog';
import Tooltip from '../general/Tooltip';
import { useNotification } from '../general/NotificationProvider';

import ColorSquare from './ColorSquare';
import ColorDetails from './ColorDetails';
import ColorDetailsEmpty from './ColorDetailsEmpty';
import PaletteCreate from './PaletteCreate';
import ConfirmDialog from '../general/ConfirmDialog';
import Modal from '../general/Modal';
import NewPalette from '@/assets/CreateIcon.svg';
import ScrapbookIcon from '@/assets/scissors-light.svg';
import DownloadButton from '@/assets/downloadSimpleLight.svg';
import TrashIcon from '@/assets/trash-light.svg';
import { isColorDark } from '../../utils/color-library-utils';

export interface MyPalettesProps {
  data: ColorLibraryData;
  onUpdatePalette: (palette: Palette) => void;
  onDeletePalette: (paletteId: string) => void;
  onDownloadPalette: (palette: Palette) => void;
  onDownloadColor: (colors: Color[]) => void;
}

const MyPalettes: React.FC<MyPalettesProps> = ({
  data,
  onUpdatePalette,
  onDeletePalette,
  onDownloadPalette,
  onDownloadColor,
}) => {
  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [selectedPalette, setSelectedPalette] = useState<Palette | null>(null);
  const [selectedColors, setSelectedColors] = useState<Color[]>([]);
  const [windowWidth, setWindowWidth] = useState(1440);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [paletteToSave, setPaletteToSave] = useState<Palette | null>(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const createPaletteRef = useRef<{ openDialog: () => void }>(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const handleDownloadPalette = (palette: Palette) => {
    onDownloadPalette(palette);
  };

  const handleCreateNewPalette = (name: string) => {
    const newPalette: Palette = {
      id: null,
      name,
      colours: [],
    };
    onUpdatePalette(newPalette);
  };

  const handleSetColorAndPalette = (color: Color, palette: Palette) => {
    if (windowWidth < 768) {
      setSelectedColor(color);
      setSelectedPalette(palette);
      setShowColorModal(true);
    } else {
      setSelectedColor(color);
      setSelectedPalette(palette);
      setSelectedColors([color]);
    }
  };

  const handleRemoveColorFromPalette = (colorId: string) => {
    if (selectedPalette) {
      const updatedPalette = {
        ...selectedPalette,
        colours: selectedPalette.colours.filter((c) => c.id !== colorId),
      };
      onUpdatePalette(updatedPalette);
      setSelectedColor(null);
    }
  };

  const handleDeletePalette = (paletteId: string) => {
    onDeletePalette(paletteId);
  };

  const handleSaveToScrapbook = (palette: Palette) => {
    if (!user.userId) return;
    setPaletteToSave(palette);
    setShowFolderDialog(true);
  };

  const handleSaveToFolder = async (selectedFolderId: number) => {
    if (!paletteToSave) return;
    try {
      const paletteData = {
        path: `/palette.${paletteToSave.id}.json`,
        contentType: 'application/x.stylus.colour.palette+json',
        content: JSON.stringify(paletteToSave),
      };

      const result = await saveToScrapbook(selectedFolderId, paletteData, user.userId);

      if (result?.succeeded) {
        showNotification(result.message || 'Color saved to scrapbook successfully', 'savesuccess');
      } else {
        showNotification('Call to scrapbook server failed.', 'error');
      }
    } catch {
      showNotification('Call to scrapbook server failed.', 'error');
    }
    setShowFolderDialog(false);
    setPaletteToSave(null);
  };

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  return (
    <div className="px-3 sm:px-5 lg:px-0">
      <div className="mb-4 sm:mb-6">
        <div className="grid grid-cols-12">
          <div className="col-span-12 sm:col-span-8 md:col-span-6">
            <button
              onClick={() => createPaletteRef.current?.openDialog()}
              className={`flex cursor-pointer items-center rounded-full border bg-transparent font-semibold text-black ${
                isMobile ? 'h-10 px-3 py-2 text-sm' : 'h-11 px-4 py-3 text-base'
              }`}
              style={{ borderColor: 'rgba(0, 0, 0, 0.2)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#cccccc30')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <NewPalette className={`${isMobile ? 'mr-1 h-4 w-4' : 'mr-2 h-5 w-5'}`} />
              Create New Palette
            </button>
          </div>
        </div>
      </div>

      <div className={isMobile ? '' : 'grid grid-cols-12 gap-4 lg:gap-6'}>
        <div className={isMobile ? '' : isTablet ? 'col-span-7' : 'col-span-8 lg:col-span-9'}>
          {data.palettes.map((palette) => (
            <div key={palette.id} className="mb-6 cursor-pointer sm:mb-8">
              <div className="mb-3 flex items-center justify-between px-2 sm:mb-4 sm:px-3">
                <span
                  className={`font-light transition-all duration-200 ${
                    isMobile ? 'text-lg sm:text-xl' : isTablet ? 'text-2xl' : 'text-2xl lg:text-4xl'
                  } ${selectedPalette?.id === palette.id ? 'font-bold' : ''}`}
                >
                  {palette.name}
                </span>

                <div
                  className="chip-palette flex items-center border bg-transparent px-2 py-1"
                  style={{ borderColor: 'rgba(0, 0, 0, 0.2)', borderRadius: '16px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#cccccc30')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div className="flex items-center justify-center px-2">
                    <Tooltip placement="top" content="Download">
                      <button
                        onClick={() => handleDownloadPalette(palette)}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent p-0"
                        type="button"
                      >
                        <DownloadButton className="h-5 w-5" />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="mx-1">
                    <div className="h-4 w-px" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }} />
                  </div>

                  <div className="flex items-center justify-center px-1">
                    <Tooltip placement="top" content="Save to Scrapbook">
                      <button
                        onClick={() => handleSaveToScrapbook(palette)}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent p-0"
                        type="button"
                      >
                        <ScrapbookIcon className="h-5 w-5" />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="mx-1">
                    <div className="h-4 w-px" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }} />
                  </div>

                  <div className="flex items-center justify-center px-2">
                    <ConfirmDialog
                      question="Are you sure you want to delete this palette?"
                      onConfirm={() => handleDeletePalette(palette.id!)}
                    >
                      <Tooltip placement="top" content="Delete Palette">
                        <button
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent p-0"
                          type="button"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </Tooltip>
                    </ConfirmDialog>
                  </div>
                </div>
              </div>

              {isMobile && (
                <div className="mb-4">
                  <h3 className="mb-2 text-base font-medium sm:text-lg">Selected Colors:</h3>
                  {selectedColors.length > 0 ? (
                    <div className="space-y-2">
                      {selectedColors.map((color) => (
                        <div
                          key={color.id}
                          className="block rounded-lg p-2 text-sm transition-all duration-200"
                          style={{
                            backgroundColor: `#${color.hexCode}`,
                            color: 'white',
                          }}
                        >
                          {color.displayName}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No colors selected</p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap">
                {palette.colours.map((color) => (
                  <ColorSquare
                    key={color.id}
                    color={color}
                    selectedColors={selectedColors}
                    hideColorAddition={true}
                    showRemoveButton={true}
                    showCreatePaletteText={true}
                    clickedPalette={selectedPalette}
                    onClick={() => handleSetColorAndPalette(color, palette)}
                    onRemoveColorFromPalette={handleRemoveColorFromPalette}
                    onDownloadColor={onDownloadColor}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {!isMobile && (
          <div className={isTablet ? 'col-span-5' : 'col-span-4 lg:col-span-3'}>
            <div className="sticky top-[10%]">
              {selectedColor ? (
                <ColorDetails
                  color={selectedColor}
                  palettes={data.palettes}
                  showRemoveButton={true}
                  showCreatePaletteText={true}
                  clickedPalette={selectedPalette || null}
                  onRemoveColorFromPalette={handleRemoveColorFromPalette}
                  onDownloadColor={onDownloadColor}
                />
              ) : (
                <ColorDetailsEmpty data={data} />
              )}
            </div>
          </div>
        )}
      </div>

      <PaletteCreate ref={createPaletteRef} onCreateNewPalette={handleCreateNewPalette} />

      {/* Mobile Color Details Modal */}
      {isMobile && (
        <Modal
          open={showColorModal}
          onClose={() => setShowColorModal(false)}
          contanerClassName="max-w-sm mx-4 rounded-xl overflow-hidden shadow-2xl"
        >
          {selectedColor && (
            <div className="relative">
              <button
                onClick={() => setShowColorModal(false)}
                className="absolute top-4 right-4 z-10 cursor-pointer text-2xl leading-none transition-all duration-200 hover:scale-110 hover:opacity-70"
                style={{ color: isColorDark(selectedColor.hexCode) ? 'white' : 'black' }}
              >
                ×
              </button>
              <ColorDetails
                color={selectedColor}
                palettes={data.palettes}
                showRemoveButton={true}
                showCreatePaletteText={true}
                showSaveToScrapbookButton={false}
                clickedPalette={selectedPalette || null}
                onRemoveColorFromPalette={handleRemoveColorFromPalette}
                onDownloadColor={onDownloadColor}
              />
            </div>
          )}
        </Modal>
      )}

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={handleSaveToFolder}
        reportTitle={paletteToSave?.name || 'Palette'}
      />
    </div>
  );
};

export default MyPalettes;
